import { Link } from 'react-router-dom';

interface ComicCardProps {
  id: number;
  slug: string;
  title: string;
  author: string;
  cover: string;
  rating: number;
  chapters: number;
  status: string;
  categoryName: string;
  updatedAt: string;
  views: number;
  variant?: 'default' | 'featured';
}

export default function ComicCard({
  slug,
  title,
  author,
  cover,
  rating,
  chapters,
  status,
  categoryName,
  updatedAt,
  views,
  variant = 'default',
}: ComicCardProps) {
  const formatViews = (v: number) => {
    if (v >= 10000) {
      return `${(v / 10000).toFixed(1)}万`;
    }
    return v.toString();
  };

  if (variant === 'featured') {
    return (
      <Link
        to={`/comic/${slug}`}
        className="group block relative overflow-hidden rounded-xl cursor-pointer"
      >
        <div className="aspect-[3/4] relative overflow-hidden rounded-xl">
          <img
            src={cover}
            alt={title}
            title={`${title} - 韩漫画免费阅读`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent-500 text-background-50">
                {categoryName}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                status === '连载中' ? 'bg-primary-500/80 text-background-50' : 'bg-foreground-500/80 text-background-50'
              }`}>
                {status}
              </span>
            </div>
            <h3 className="text-white font-semibold text-base md:text-lg leading-tight mb-1">
              {title}
            </h3>
            <p className="text-white/70 text-xs">{author}</p>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/comic/${slug}`}
      className="group block cursor-pointer"
    >
      <div className="relative overflow-hidden rounded-lg mb-3">
        <div className="aspect-[3/4] relative overflow-hidden rounded-lg bg-background-200">
          <img
            src={cover}
            alt={title}
            title={`${title} - 韩漫画免费阅读`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute top-2 left-2">
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary-500 text-background-50">
              {rating.toFixed(1)}分
            </span>
          </div>
          <div className="absolute top-2 right-2">
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-50/90 text-foreground-700">
              {chapters}话
            </span>
          </div>
        </div>
      </div>

      <h3 className="text-sm md:text-base font-semibold text-foreground-950 leading-tight mb-1 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
        {title}
      </h3>

      <div className="flex items-center gap-3 text-xs text-foreground-400">
        <span>{author}</span>
        <span className="text-foreground-300">·</span>
        <span>{categoryName}</span>
      </div>

      <div className="mt-1.5 flex items-center gap-3 text-[11px] text-foreground-400">
        <span className="flex items-center gap-1">
          <i className="ri-eye-line text-xs"></i>
          {formatViews(views)}
        </span>
        <span className="text-foreground-300">·</span>
        <span>更新于 {updatedAt}</span>
      </div>
    </Link>
  );
}